import { dollarsToCents } from '../../../utils/formatters';
import type { FormState } from './types';

/**
 * The body for the manage-load save.
 *
 * Most fields collapse an empty string to `undefined` rather than sending it:
 * the update route falls back to the saved row for anything it is not sent, so
 * omitting a field means "leave it alone" while "" would mean "clear it".
 * `driverName` and `truckNumber` are the deliberate exceptions — they send
 * `null`, because clearing a driver is a real edit.
 */
export function buildSavePayload(form: FormState, item: any) {
  const originalProductId = item.productId ?? item.product?.id ?? '';
  const originalCustomerId = item.order?.customerId ?? '';
  return {
    orderId: item.order.id,
    itemId: item.id,
    nextStatus: form.nextStatus,
    locationId: form.locationId,
    loadOptionId: form.loadOptionId || undefined,
    loadCount: item.loadCountSnapshot || 1,
    palletSizeId: form.palletSizeId || undefined,
    priceOverrideCents: dollarsToCents(form.priceOverrideInput),
    // Dollars in, cents out — the field is dollars on every path now.
    deliveryFeeOverrideCents: dollarsToCents(form.deliveryFeeInput),
    carrierRate: dollarsToCents(form.carrierRateInput),
    // Date-only values are pinned to midnight UTC — the whole app renders the
    // UTC day, so sending local midnight would shift the paperwork by a day.
    pickupDate: form.pickupDate
      ? new Date(form.pickupDate + 'T00:00:00Z').toISOString()
      : undefined,
    deliveryDate: form.deliveryDate
      ? new Date(form.deliveryDate + 'T00:00:00Z').toISOString()
      : undefined,
    poNumber: form.poNumber.trim() || undefined,
    // Sent only when switched; the route then reassigns the order and re-derives
    // everything. undefined leaves the order's customer alone.
    customerId: form.customerId !== originalCustomerId ? form.customerId : undefined,
    ticketNumber: form.ticketNumber.trim() || undefined,
    // Only sent when it actually changed; the route treats a productId as a
    // request to re-snapshot the product. Never send an empty id (a switch
    // clears the product until the admin re-picks — the save gate blocks that).
    productId:
      form.selectedProductId && form.selectedProductId !== originalProductId
        ? form.selectedProductId
        : undefined,
    pendingCarrierId: form.pendingCarrierId,
    quantity: form.quantity,
    pickupNote: form.pickupNote.trim() || undefined,
    deliveryNote: form.deliveryNote.trim() || undefined,
    internalNotes: form.internalNotes.trim() || undefined,
    driverName: form.driverName.trim() || null,
    truckNumber: form.truckNumber.trim() || null,
    deliveryAddressId: form.deliveryAddressId || undefined,
    driverContactId: form.driverContactId,
    tags: form.tags,
  };
}

/**
 * The carrier rate configured for one delivery address at one warehouse, in
 * cents. Zero when either side is unknown — an address with no rate for the
 * chosen warehouse is normal, not an error.
 */
export function resolveCarrierRateCents(address: any, warehouseId: string): number {
  if (!address || !warehouseId) {
    return 0;
  }

  const rate = address.carrierRates?.find(
    (carrierRate: any) => carrierRate.warehouseId === warehouseId
  );

  return rate?.carrierRateCents ?? 0;
}
